import './FileUploadBoxStyle.less';

import { values } from 'lodash';
import moment from 'moment-timezone';
import React from 'react';
import Dropzone from 'react-dropzone';

import { Icon, MoreOptionsMenuButton } from '@opendoor/bricks';
import { DOCUMENT_CHECKLIST_ITEM_STATES } from '../../constants/DocumentChecklistItemConstants';
import { MIME_TYPES } from '../../constants/FileUploadConstants';
import { IDocumentChecklistItem, PermittedFileType } from '../../declarations';
import { stringToTitleCase } from '../../helpers/formatUtils';
import TextExtender from '../common/TextExtender';
import SellerStatus from './SellerStatus';

export interface IFileUploadBoxProps {
  documentChecklistItem?: IDocumentChecklistItem;
  error: boolean;
  isLoading: boolean;
  onDocumentDownload: (id: number) => void;
  onFileSelect: (file: File) => void;
  onDocumentRemove: (id: number) => void;
}

export interface IFileUploadBoxState {
  errorMessage?: string;
}

const ACCEPTED_FILE_TYPES: { [key: string]: PermittedFileType } = {
  pdf: MIME_TYPES.pdf,
};

const MAX_SIZE_IN_MEGABYTES = 100;

export default class FileUploadBox extends React.Component<
  IFileUploadBoxProps,
  IFileUploadBoxState
> {
  public static getItemDocument = (documentChecklistItem?: IDocumentChecklistItem) => {
    let document: IDocumentChecklistItem['document'] | undefined;
    if (documentChecklistItem) {
      document = documentChecklistItem.document;
    }
    return document;
  };

  public static getItemState = (documentChecklistItem?: IDocumentChecklistItem) => {
    let state: IDocumentChecklistItem['state'] | undefined;
    if (documentChecklistItem) {
      state = documentChecklistItem.state;
    }
    return state;
  };

  public static shouldShowUploadButton = (documentChecklistItem?: IDocumentChecklistItem) => {
    const state = FileUploadBox.getItemState(documentChecklistItem);
    const document = FileUploadBox.getItemDocument(documentChecklistItem);

    const showUploadButton =
      (state !== DOCUMENT_CHECKLIST_ITEM_STATES.executed &&
        state !== DOCUMENT_CHECKLIST_ITEM_STATES.received &&
        (!document ||
          state === DOCUMENT_CHECKLIST_ITEM_STATES.initial ||
          state === DOCUMENT_CHECKLIST_ITEM_STATES.needs_external_signature)) ||
      FileUploadBox.isInAdditionalDocumentsMode(documentChecklistItem);
    return showUploadButton;
  };

  public static isInAdditionalDocumentsMode = (documentChecklistItem?: IDocumentChecklistItem) => {
    return !documentChecklistItem;
  };

  public static isPending = (documentChecklistItem?: IDocumentChecklistItem) => {
    const state = FileUploadBox.getItemState(documentChecklistItem);
    return (
      state === DOCUMENT_CHECKLIST_ITEM_STATES.needs_od_signature ||
      state === DOCUMENT_CHECKLIST_ITEM_STATES.needs_review
    );
  };

  public static isApproved = (documentChecklistItem?: IDocumentChecklistItem) => {
    const state = FileUploadBox.getItemState(documentChecklistItem);
    return (
      state === DOCUMENT_CHECKLIST_ITEM_STATES.executed ||
      state === DOCUMENT_CHECKLIST_ITEM_STATES.received
    );
  };

  public state: IFileUploadBoxState = {};
  public dropZoneRef: Dropzone;

  public componentWillReceiveProps(nextProps: IFileUploadBoxProps) {
    const { error } = this.props;
    if (nextProps.error && nextProps.error !== error) {
      this.setState({ errorMessage: 'Request error. Try again.' });
    }
  }

  public handleDocumentDownload = () => {
    const { documentChecklistItem, onDocumentDownload } = this.props;
    const { document } = documentChecklistItem;

    const documentId = document && document.id;
    if (documentId) {
      onDocumentDownload(documentId);
    }
  };

  public handleDocumentRemove = () => {
    const { documentChecklistItem, onDocumentRemove } = this.props;
    const { document } = documentChecklistItem;

    const documentId = document && document.id;
    if (documentId) {
      onDocumentRemove(documentId);
    }
  };

  public handleDropAccepted = newFiles => {
    const { onFileSelect } = this.props;
    onFileSelect(newFiles[0]);
  };

  public handleDropRejected = () =>
    this.setState({
      errorMessage:
        `Invalid file. Must be a ${Object.keys(ACCEPTED_FILE_TYPES).join(', ')}` +
        ` smaller than ${MAX_SIZE_IN_MEGABYTES}MB`,
    });

  public dropzoneOpen = () => {
    this.dropZoneRef && this.dropZoneRef.open();
  };

  public renderLeftColumn = () => {
    const { documentChecklistItem } = this.props;

    const document = FileUploadBox.getItemDocument(documentChecklistItem);
    const state = FileUploadBox.getItemState(documentChecklistItem);
    const documentCreatedAt = document && document.created_at;

    const additionalDocumentsMode = FileUploadBox.isInAdditionalDocumentsMode(
      documentChecklistItem,
    );

    const showUploadButton = FileUploadBox.shouldShowUploadButton(documentChecklistItem);

    const documentName = additionalDocumentsMode
      ? 'Additional Documents'
      : stringToTitleCase(documentChecklistItem.name);

    return (
      <div className="left-column col-lg-7 col-md-12 col-xs-12 row">
        <div className="file-icon">
          <Icon name="file-pdf" />
        </div>
        <div className="item-name-container">
          <span className={`item-name ${additionalDocumentsMode ? 'additional-documents' : ''}`}>
            <TextExtender text={documentName} />
          </span>
          {document && (
            <div className="file-link" onClick={this.handleDocumentDownload}>
              {showUploadButton ? 'Download document' : document.name}
            </div>
          )}
          {documentCreatedAt && (
            <small className="uploaded-at">
              Uploaded {moment(documentCreatedAt).format('MMM D, YYYY [at] h:mma')}
            </small>
          )}
          {DOCUMENT_CHECKLIST_ITEM_STATES.needs_external_signature === state &&
            document && <SellerStatus statusText="Needs Signature" type="needs-attention" />}
        </div>
      </div>
    );
  };

  public renderRightColumn = () => {
    const { documentChecklistItem, isLoading } = this.props;

    const state = FileUploadBox.getItemState(documentChecklistItem);
    const showUploadButton = FileUploadBox.shouldShowUploadButton(documentChecklistItem);

    let moreOptionsMenuButton = null;
    if (!showUploadButton) {
      const menuItems =
        state === DOCUMENT_CHECKLIST_ITEM_STATES.executed
          ? [
              { onClick: this.handleDocumentDownload, text: 'Download' },
              {
                text:
                  'Note: Please contact your Closing Manager if you want to make adjustments ' +
                  'to this file.',
              },
            ]
          : [
              { onClick: this.dropzoneOpen, text: 'Upload new' },
              { onClick: this.handleDocumentDownload, text: 'Download current' },
              { onClick: this.handleDocumentRemove, text: 'Remove' },
            ];
      moreOptionsMenuButton = <MoreOptionsMenuButton menuItems={menuItems} />;
    }

    return (
      <div className="right-column col-lg-5 col-md-12 col-xs-12">
        {isLoading ? (
          <div className="col-md-offset-2 loading-spinner-medium" />
        ) : FileUploadBox.shouldShowUploadButton(documentChecklistItem) ? (
          <div className="upload row">
            <button className="upload-button" onClick={this.dropzoneOpen}>
              Upload
            </button>
          </div>
        ) : (
          <div className="status-and-more-options-container row">
            {FileUploadBox.isApproved(documentChecklistItem) && (
              <SellerStatus type="approved" statusText="Seller Reviewed" />
            )}
            {FileUploadBox.isPending(documentChecklistItem) && (
              <SellerStatus type="pending" statusText="Seller Review Pending" />
            )}
            {moreOptionsMenuButton}
          </div>
        )}
      </div>
    );
  };

  public render(): JSX.Element {
    const { children, documentChecklistItem } = this.props;
    const { errorMessage } = this.state;

    const state = FileUploadBox.getItemState(documentChecklistItem);
    const additionalDocumentsMode = FileUploadBox.isInAdditionalDocumentsMode(
      documentChecklistItem,
    );

    const content = (
      <div>
        <div className={`content row ${errorMessage ? 'error' : ''}`}>
          {this.renderLeftColumn()}
          {this.renderRightColumn()}
        </div>
        <div className="children-container">{children}</div>
      </div>
    );

    const errorContent = errorMessage ? (
      <div className="error-message">
        <Icon name="ic-exclamation-point" />
        {errorMessage}
      </div>
    ) : null;

    const contentClass = `content-container ${errorMessage ? 'error' : ''}`;

    return (
      <div className="resale-closing-dashboard-file-upload">
        {state !== DOCUMENT_CHECKLIST_ITEM_STATES.executed || additionalDocumentsMode ? (
          <Dropzone
            accept={values(ACCEPTED_FILE_TYPES).join()}
            acceptClassName="accepted"
            activeClassName="active"
            className={contentClass}
            disableClick={true}
            ref={r => (this.dropZoneRef = r)}
            rejectClassName="error"
            onDropAccepted={this.handleDropAccepted}
            onDropRejected={this.handleDropRejected}
            maxSize={Math.pow(MAX_SIZE_IN_MEGABYTES, 6)} // 100 MB
          >
            {content}
          </Dropzone>
        ) : (
          <div className={contentClass}>{content}</div>
        )}
        {errorContent}
      </div>
    );
  }
}
